import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, Globe } from 'lucide-react';
import { useCreateMoodboard } from '@/hooks/use-moodboards';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import type { SaveToMoodboardPayload } from '@/types/moodboards';

interface CreateMoodboardDialogProps {
  isOpen: boolean;
  onClose: () => void;
  initialItem?: SaveToMoodboardPayload;
}

export function CreateMoodboardDialog({ isOpen, onClose, initialItem }: CreateMoodboardDialogProps) {
  const [name, setName] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const createMoodboard = useCreateMoodboard();

  const handleCreate = async () => {
    if (!name.trim()) return;

    await createMoodboard.mutateAsync({
      name: name.trim(),
      icon: '🎭', // Always use theater masks
      is_private: isPrivate,
      initialItem,
    });

    setName('');
    setIsPrivate(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10003] bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Dialog - Bottom Drawer Style */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed z-[10004] bottom-0 left-0 right-0 max-w-2xl mx-auto"
          >
            <div className="bg-[#0A0A0B] rounded-t-3xl shadow-2xl overflow-hidden border-t border-x border-white/10 max-h-[75vh] overflow-y-auto">
              {/* Handle */}
              <div className="flex items-center justify-center py-3">
                <div className="w-12 h-1.5 bg-white/20 rounded-full" />
              </div>
              {/* Header */}
              <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">Create Moodboard</h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-full hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Preview Image */}
                {initialItem?.image_url && (
                  <div className="relative rounded-xl overflow-hidden aspect-video">
                    <img
                      src={initialItem.image_url}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-3 left-3 right-3">
                      <p className="text-white text-sm font-medium truncate">
                        First item in your moodboard
                      </p>
                    </div>
                  </div>
                )}

                {/* Name Input */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-white">Name</Label>
                  <Input
                    id="name"
                    placeholder="My Moodboard"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreate();
                    }}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    autoFocus
                  />
                </div>

                {/* Privacy Toggle */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10">
                  <div className="flex items-center gap-3">
                    {isPrivate ? (
                      <Lock className="w-5 h-5 text-gray-400" />
                    ) : (
                      <Globe className="w-5 h-5 text-gray-400" />
                    )}
                    <div>
                      <p className="font-medium text-sm text-white">
                        {isPrivate ? 'Private' : 'Public'}
                      </p>
                      <p className="text-xs text-gray-400">
                        {isPrivate
                          ? 'Only you can see this moodboard'
                          : 'Anyone with the link can view'}
                      </p>
                    </div>
                  </div>
                  <Switch checked={isPrivate} onCheckedChange={setIsPrivate} />
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-white/10 flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={onClose} 
                  className="flex-1 border-white/10 hover:bg-white/10 text-white"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={!name.trim() || createMoodboard.isPending}
                  className="flex-1 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white"
                >
                  {createMoodboard.isPending ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
